// ── Participant Dashboard (Wave 9) — the participant's complete self-service home ─
// The ONLY surface a participant ever sees, and the page they land on at /dashboard.
// Decomposed into per-section components (each < 500 LOC, each wrapping its OWN
// useHonestQuery -> HonestState). @vjal/ui + navy/ink tokens only; humanizeLabel
// via the shared helpers; localized session time in the participant's timezone.
//
// ── ABSOLUTE RELEASE GATES (asserted by the co-located biting tests) ─────────
//   • FR-PDASH-001 PARTICIPANT ISOLATION — every section reads ONLY the
//     authenticated participant's OWN data (own profile, own cohorts (scope=mine),
//     own cohort's sessions/recordings/assignments). NO roster, NO peer record,
//     NO other cohort, NO admin-surface bleed. Sessions are re-filtered to the
//     own-cohort set (defense-in-depth). The participant has NO Programs /
//     Resources / Reports / Companies / Users / Sessions-admin / Capstone-admin /
//     Certificates-admin module (route + nav denied in prior waves; re-asserted).
//   • FR-REPORT-001 REPORT EXCLUSION — ZERO reports anywhere: no report content,
//     status, existence, link, or notification. NO report endpoint is read.
//   • NO FABRICATION — dark/missing lanes (recordings, capstone, certificate)
//     render honest unavailable/not-available/not-configured, never fake data.
//
// FAIL-CLOSED: this projection renders ONLY for `participant`. Any other role
// that reaches it (it shouldn't — the surface factory picks it only for
// participant) gets permission-denied with NO learner data.

import { HonestState, useHonestQuery } from "../../components/honest-state";
import { useWalkReadyMarker } from "../../lib/walk-readiness";
import type { CoachingProfileDto } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { isParticipantDashboardRole, participantQueryKeys, readOwnProfile } from "./participant-dashboard-helpers";
import { resolveParticipantZone } from "./participant-time";
import { ParticipantOverviewSection } from "./participant-overview-section";
import { ParticipantSessionsSection } from "./participant-sessions-section";
import { ParticipantRecordingsSection } from "./participant-recordings-section";
import { ParticipantAssignmentsSection } from "./participant-assignments-section";
import { ParticipantCapstoneSection } from "./participant-capstone-section";
import { ParticipantCertificateSection } from "./participant-certificate-section";
import { ParticipantProfileSection } from "./participant-profile-section";

export function ParticipantDashboardPage({ role }: ProductScreenContext) {
  // Fail-closed: a non-participant role renders ONLY permission-denied (no learner
  // projection). The surface factory only selects this body for participant, so
  // this is belt-and-suspenders. Each branch is its own component so the
  // own-data hooks below stay unconditional (rules-of-hooks).
  if (!isParticipantDashboardRole(role)) {
    return <ParticipantDashboardDenied />;
  }
  return <ParticipantDashboardBody />;
}

/**
 * The fail-closed denial. Split out so the own-data hooks in the body stay
 * unconditional (rules-of-hooks) while this surface can still own its own
 * walk-readiness flip — a denial IS terminal, and the route shell delegates the
 * dashboard marker to whatever body renders here.
 */
function ParticipantDashboardDenied() {
  useWalkReadyMarker(true);
  return (
    <section aria-label="Dashboard" className="col-span-12" data-testid="participant-dashboard-denied">
      <div className="rounded-xl border border-line bg-white">
        <HonestState
          posture="permission-denied"
          title="This dashboard isn't available for your role"
          message="The participant dashboard shows a learner's own coaching home. Your role has its own dashboard."
        />
      </div>
    </section>
  );
}

function ParticipantDashboardBody() {
  // One own-profile read resolves the participant's timezone for localized session
  // times (the sections share this cached read via the same query key). The zone
  // falls back to the session zone then the browser if the profile lacks one.
  const profileQuery = useHonestQuery<CoachingProfileDto>(
    participantQueryKeys.profile(),
    readOwnProfile
  );
  const zone = resolveParticipantZone(profileQuery.data?.timezone, null);
  // Walk-readiness marker, phase 2 (see src/lib/walk-readiness.ts). The route
  // shell delegates the dashboard flip; this projection is terminal once the own
  // profile read settles. That read is the projection's primary one — it drives
  // the "Welcome, <name>" heading and the session timezone, and the overview
  // section shares its cache key, so its settle is the hero's settle. Every
  // settled posture counts (ready, empty, permission-denied, unavailable): the
  // honest error surface is terminal too. Section-level reads below fan out from
  // here and render their own honest states inside an already-settled shell.
  useWalkReadyMarker(profileQuery.posture !== "loading");

  return (
    <section
      aria-label="Participant dashboard"
      className="col-span-12 flex flex-col gap-4"
      data-testid="participant-dashboard"
    >
      <ParticipantOverviewSection />
      <ParticipantSessionsSection zone={zone} />
      <ParticipantRecordingsSection />
      <ParticipantAssignmentsSection />
      <ParticipantCapstoneSection />
      <ParticipantCertificateSection />
      <ParticipantProfileSection />
    </section>
  );
}
